/**
 * Reclaim flow (Shopify port of Webflow/reclaim.js).
 *
 * URL: /pages/claim?id=<itemId>  (also /sv/... and /da/... via Shopify markets)
 *
 * Flow:
 *  1. GET <RECLAIM_API_BASE>/api/items/:id -> populate intro + banner
 *  2. If item.returnQrCode -> show PostNord QR state
 *     Else if item.reclaim.status -> show thank-you state
 *     Else -> intro -> form -> submit -> thank-you
 *  3. On submit: POST <RECLAIM_API_BASE>/api/items/:id/reclaim
 *     Uploads any selected images via /api/items/:tempId/uploadImage first.
 *
 * Localisation: all visible strings come from window.reclaimLocale (set in
 * sections/reclaim-form.liquid). Date format follows window.reclaimLocale.date_locale.
 */
(function () {
  'use strict';

  // The Shopify theme's window.API_BASE_URL points at /api/shopify, but the
  // reclaim & uploadImage endpoints currently live under /api on the legacy
  // surface. Calling the root host directly keeps parity with the Webflow JS
  // until we expose the same endpoints under /api/shopify.
  var RECLAIM_API_BASE = 'https://api.mairesale.com';
  var MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  var MAX_IMAGE_DIMENSION = 3024;

  var locale = window.reclaimLocale || {};
  var dateLocale = locale.date_locale || 'sv-SE';
  var itemCurrency = 'SEK';
  var soldPriceAmount = 0;

  // --------------------------------------------------------------------------
  // Small helpers
  // --------------------------------------------------------------------------

  function $(id) {
    return document.getElementById(id);
  }

  function getParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  function formatTemplate(template, vars) {
    if (!template) return '';
    return Object.keys(vars).reduce(function (acc, key) {
      // Locale strings ship with sentinels like __DATE__, __CONTACT__, __AMOUNT__.
      // We also support the {{ key }} form for safety.
      var sentinel = '__' + key.toUpperCase() + '__';
      return acc.split(sentinel).join(vars[key]).replace(
        new RegExp('\\{\\{\\s*' + key + '\\s*\\}\\}', 'g'),
        vars[key]
      );
    }, template);
  }

  function show(id, displayValue) {
    var el = $(id);
    if (el) el.style.display = displayValue || 'block';
  }

  function hide(id) {
    var el = $(id);
    if (el) el.style.display = 'none';
  }

  function showButtonBar() {
    var bar = $('reclaimButtonBar');
    if (bar) bar.classList.add('reclaim-button-bar--visible');
  }

  // Pin the button bar to the viewport bottom for every step except the intro.
  // The intro keeps the buttons in-flow; cancelling reloads the page, so we
  // only ever need to enable this (never toggle it back off).
  function enableStickyBar() {
    var section = getSectionRoot();
    if (section) section.classList.add('reclaim--sticky-bar');
  }

  function hideAllButtons() {
    ['resellButton', 'reclaimButton', 'nextButton', 'cancelButton', 'doneButton', 'toMaiButton']
      .forEach(hide);
  }

  // --------------------------------------------------------------------------
  // API helpers
  // --------------------------------------------------------------------------

  function getItem(itemId) {
    return fetch(RECLAIM_API_BASE + '/api/items/' + encodeURIComponent(itemId))
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)); })
      .then(function (res) {
        console.log('[reclaim] item response:', res);
        // Accept both envelopes: { data: { ...item } } (Webflow callBackendApi
        // shape) and the bare item object that the Cloud Run backend returns.
        var data = (res && (res.data || res)) || {};
        data.id = itemId;
        return data;
      });
  }

  function postReclaim(itemId, reclaim) {
    return fetch(RECLAIM_API_BASE + '/api/items/' + encodeURIComponent(itemId) + '/reclaim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reclaim: reclaim })
    }).then(function (r) {
      if (!r.ok) return Promise.reject(new Error('HTTP ' + r.status));
      return r.json();
    });
  }

  // Minimal image scaler: only re-encodes when the file is >5MB. Keeps the
  // payload similar to what the legacy uploader produces for big iPhone shots.
  function scaleImageIfNeeded(file) {
    if (file.size < MAX_IMAGE_BYTES || typeof createImageBitmap !== 'function') {
      return Promise.resolve(file);
    }
    return createImageBitmap(file).then(function (bitmap) {
      var ratio = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(bitmap.width, bitmap.height));
      var w = Math.round(bitmap.width * ratio);
      var h = Math.round(bitmap.height * ratio);
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
      return new Promise(function (resolve) {
        canvas.toBlob(function (blob) {
          resolve(blob || file);
        }, 'image/jpeg', 0.85);
      });
    }).catch(function () { return file; });
  }

  function uploadOneImage(itemId, file, index) {
    return scaleImageIfNeeded(file).then(function (blob) {
      var form = new FormData();
      form.append('itemId', itemId);
      form.append('fileName', 'reclaim_' + itemId + '_' + index);
      form.append('file', blob, 'reclaim_' + itemId + '_' + index + '.jpg');
      form.append('temporary', 'false');
      form.append('generateSmallImage', 'true');
      return fetch(RECLAIM_API_BASE + '/api/items/' + encodeURIComponent(itemId) + '/uploadImage', {
        method: 'POST',
        body: form
      }).then(function (r) {
        if (!r.ok) return Promise.reject(new Error('HTTP ' + r.status));
        return r.json();
      });
    });
  }

  // --------------------------------------------------------------------------
  // Page setup
  // --------------------------------------------------------------------------

  function getCoverImageUrl(item) {
    var images = (item && item.images) || {};
    if (window.innerWidth <= 400) {
      return images.modelImage
        || images.enhancedFrontImageSmall
        || images.enhancedFrontImage
        || images.frontImageSmall
        || images.frontImage
        || '';
    }
    return images.modelImage
      || images.enhancedFrontImage
      || images.frontImage
      || '';
  }

  function formatSoldDate(soldDate) {
    try {
      return new Date(soldDate).toLocaleDateString(dateLocale, {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch {
      return '';
    }
  }

  // Returns the number of weekdays (Mon–Fri) between two dates, exclusive of start.
  function weekdaysBetween(from, to) {
    var days = 0;
    var cursor = new Date(from);
    cursor.setHours(0, 0, 0, 0);
    var end = new Date(to);
    end.setHours(0, 0, 0, 0);
    while (cursor < end) {
      cursor.setDate(cursor.getDate() + 1);
      var dow = cursor.getDay();
      if (dow !== 0 && dow !== 6) days++;
    }
    return days;
  }

  function isReclaimWindowClosed(item) {
    var collectedRaw = item.shipmentCollectedDate;
    if (!collectedRaw) return false; // no collected date → allow (safety fallback)
    var collectedDate = new Date(collectedRaw);
    if (isNaN(collectedDate.getTime())) return false;
    return weekdaysBetween(collectedDate, new Date()) > 5;
  }

  // Support agents can send a reclaim link that bypasses the 5-day window via
  // ?allowLate=1 (or ?allowLate=<agent tag>). The window is advisory only — the
  // reclaim is still reviewed and approved manually — so this just re-enables the
  // form past the deadline. The tag is recorded on the submitted reclaim.
  function lateReclaimOverride() {
    return getParam('allowLate');
  }

  function computeKeepDiscountAmount(soldPrice) {
    var amount = Math.round((soldPrice || 0) * 0.1);
    if (amount < 60) return 60;
    if (amount > 250) return 250;
    return amount;
  }

  function initializePage(item) {
    var title = ((item.cleanedBrand || item.brand) + ' ' + (item.category || '')).trim();
    $('itemTitle').innerText = title;
    $('itemTitleBanner').innerText = title;

    var date = formatSoldDate(item.soldDate);
    var hasPlatform = item.soldPlatform && item.soldPlatform !== 'Other';
    var platform = item.soldPlatform || '';
    if (platform === 'Mai Shop') platform = 'Mai';
    var subtitle = formatTemplate(
      hasPlatform ? locale.subtitle_purchased_via : locale.subtitle_purchased_on,
      { date: date, platform: platform }
    );
    $('itemSubtitle').innerText = subtitle;
    $('itemSubtitleBanner').innerText = subtitle;

    var imageUrl = getCoverImageUrl(item);
    if (imageUrl) {
      $('itemImage').src = imageUrl;
      $('itemImageBanner').src = imageUrl;
    }

    $('resellButton').href = 'https://sell.mairesale.com/sell-item/?id=' + item.id + '&type=resell';
    show('resellButton', 'flex');

    var reclaimBtn = $('reclaimButton');
    if (isReclaimWindowClosed(item) && !lateReclaimOverride()) {
      reclaimBtn.disabled = true;
      reclaimBtn.style.opacity = '0.4';
      reclaimBtn.style.cursor = 'not-allowed';
      reclaimBtn.title = locale.reclaim_window_closed || 'Reklamationsfönstret är stängt';
      var notice = document.createElement('p');
      notice.id = 'reclaimWindowNotice';
      notice.style.cssText = 'font-size:1.3rem;color:rgba(var(--color-foreground),0.55);text-align:center;margin:0.8rem 0 0;';
      notice.innerText = locale.reclaim_window_closed_notice || 'Reklamationsperioden på 5 vardagar har passerat.';
      var introCopy = document.querySelector('.reclaim-intro-copy');
      if (introCopy) introCopy.appendChild(notice);
    }
    show('reclaimButton', 'flex');
    showButtonBar();

    itemCurrency = item.currency || item.soldCurrency || 'SEK';

    var soldPrice = Math.round(item.soldPrice || 0);
    soldPriceAmount = soldPrice;
    $('fullRefundText').innerText = formatTemplate(
      locale.compensation_option_return,
      { amount: String(soldPrice), currency: itemCurrency }
    );

    var compensation = computeKeepDiscountAmount(item.soldPrice);
    $('discount10PercentText').innerText = formatTemplate(
      locale.compensation_option_keep_discount,
      { amount: String(compensation) }
    );

    var customInput = $('customDiscountAmount');
    if (customInput) {
      customInput.placeholder = itemCurrency === 'DKK'
        ? locale.custom_discount_placeholder_dkk
        : locale.custom_discount_placeholder_sek;
    }

    var buyer = item.buyer || {};
    var contact = buyer.Email || buyer.PhoneNumber || '';
    var thankYouTpl = contact.indexOf('@') !== -1 ? locale.reply_via_email : locale.reply_via_sms;
    $('thankYouText').innerText = formatTemplate(thankYouTpl, { contact: contact });

    if (item.returnQrCode) {
      // Backend already generated a return label — straight to QR state.
      var section = getSectionRoot();
      if (section) section.classList.add('reclaim--qr-state');
      showButtonBar();
      enableStickyBar();
      hideAllButtons();
      show('toMaiButton', 'flex');
      hide('introDiv');
      hide('thankYouDiv');
      show('itemBanner', 'flex');
      $('qrCodeImage').src = item.returnQrCode;
      if (item.returnQrCodePage) {
        $('postnordQrCodeLink').href = item.returnQrCodePage;
      }
      show('postnordQrCodeDiv', 'flex');
      $('shippingText').innerText = formatTemplate(locale.after_qr_shipping, { contact: contact });
    } else if (item.reclaim && item.reclaim.status) {
      // Reclaim already submitted — show thank-you, no form.
      showButtonBar();
      enableStickyBar();
      hideAllButtons();
      show('toMaiButton', 'flex');
      hide('introDiv');
      show('thankYouDiv', 'block');
      show('itemBanner', 'flex');
    }
  }

  // --------------------------------------------------------------------------
  // Event wiring
  // --------------------------------------------------------------------------

  // Per-reason description copy: an imperative placeholder while empty, and a
  // noun-form label once the field has a value.
  function setDescriptionCopy(labelText, placeholderText) {
    var el = $('reclaimDescriptionLabel');
    if (el && labelText) el.innerText = labelText;
    var desc = $('reclaimDescription');
    if (desc && placeholderText) desc.placeholder = placeholderText;
  }

  // The description label is hidden until the textarea has a value, mirroring the
  // way the dropdown labels appear once an option is selected.
  function refreshDescriptionLabel() {
    var label = $('descriptionLabel');
    var desc = $('reclaimDescription');
    if (!label || !desc) return;
    label.style.visibility = desc.value.trim() ? 'visible' : 'hidden';
  }

  // Hide and clear every reason-specific field. Used both when switching reason
  // and when the user picks the empty "Välj anledning" placeholder again.
  function resetReasonSubFields() {
    document.querySelectorAll('.simple-input-container').forEach(function (el) {
      if (el.id !== 'reclaimReasonContainer') el.style.display = 'none';
    });
    hide('reclaimImagesContainer');
    hide('compensationPreferenceDiv');

    var description = $('reclaimDescription');
    var defectType  = $('reclaimDefectType');
    var listingError = $('reclaimListingError');
    if (description)  { description.required = false; description.value = ''; }
    if (defectType)   { defectType.required = false;  defectType.value = ''; }
    if (listingError) { listingError.required = false; listingError.value = ''; }
    refreshDescriptionLabel();
  }

  function onReasonChange(e) {
    var reason = e.target.value;
    var reasonLabel = $('reasonLabel');
    if (!reason) {
      if (reasonLabel) reasonLabel.style.visibility = 'hidden';
      e.target.style.color = '#929292';
      resetReasonSubFields();
      return;
    }
    if (reasonLabel) reasonLabel.style.visibility = 'visible';
    e.target.style.color = '#101010';

    resetReasonSubFields();

    var description = $('reclaimDescription');
    var defectType  = $('reclaimDefectType');
    var listingError = $('reclaimListingError');

    if (reason === 'UndisclosedDefects') {
      show('reclaimDefectTypeContainer');
      if (defectType) defectType.required = true;
      show('reclaimDescriptionContainer');
      if (description) description.required = true;
      show('reclaimImagesContainer');
      setDescriptionCopy(locale.description_label_defect, locale.description_placeholder_defect);
      var imagesTitleEl = $('imagesTitle');
      if (imagesTitleEl) imagesTitleEl.innerText = locale.images_title_required;
    } else if (reason === 'IncorrectListing') {
      show('reclaimListingErrorContainer');
      if (listingError) listingError.required = true;
      // Both a free-text field and photos help us assess and later resell.
      show('reclaimDescriptionContainer');
      show('reclaimImagesContainer');
      setDescriptionCopy(locale.description_label_listing, locale.description_placeholder_listing);
      var imagesTitleEl3 = $('imagesTitle');
      if (imagesTitleEl3) imagesTitleEl3.innerText = locale.images_title;
    } else if (reason === 'WrongItem') {
      show('reclaimDescriptionContainer');
      if (description) description.required = true;
      show('reclaimImagesContainer');
      setDescriptionCopy(locale.description_label_default, locale.description_placeholder_default);
      var imagesTitleEl2 = $('imagesTitle');
      if (imagesTitleEl2) imagesTitleEl2.innerText = locale.images_title_wrong_item;
    } else if (reason === 'Other') {
      show('reclaimDescriptionContainer');
      if (description) description.required = true;
      setDescriptionCopy(locale.description_label_default, locale.description_placeholder_default);
    }
    // NotArrived: nothing to describe — no comment field, no images.
    // "Visible to the seller" note: relevant for every reason that involves a
    // seller-facing description, i.e. all of them except NotArrived.
    var descHelp = $('reclaimDescriptionHelp');
    if (descHelp) descHelp.style.display = reason === 'NotArrived' ? 'none' : 'block';

    configureCompensationForReason(reason);
    show('compensationPreferenceDiv', 'block');
  }

  // NotArrived means the buyer never received the item, so keeping it or taking
  // a discount makes no sense — only a full refund applies. Other reasons keep
  // the full three-way choice.
  function configureCompensationForReason(reason) {
    var keepDiscountRow = $('keepDiscountRow');
    var customDiscountRow = $('customDiscountRow');
    var customContainer = $('customDiscountContainer');
    var fullRefundRow = $('fullRefundRow');
    var fullRefundRadio = fullRefundRow ? fullRefundRow.querySelector('input') : null;
    var fullRefundLabel = $('fullRefundText');
    var notArrived = reason === 'NotArrived';

    if (keepDiscountRow) keepDiscountRow.style.display = notArrived ? 'none' : '';
    if (customDiscountRow) customDiscountRow.style.display = notArrived ? 'none' : '';
    if (notArrived && customContainer) customContainer.style.display = 'none';

    if (fullRefundRadio) {
      fullRefundRadio.value = notArrived ? 'Full refund' : 'Full refund and return';
      // Auto-select for NotArrived (only option); otherwise keep the user's choice.
      if (notArrived) fullRefundRadio.checked = true;
    }
    if (fullRefundLabel) {
      fullRefundLabel.innerText = formatTemplate(
        notArrived ? locale.compensation_option_full_refund : locale.compensation_option_return,
        { amount: String(soldPriceAmount), currency: itemCurrency }
      );
    }
  }

  function onDefectTypeChange(e) {
    var value = e.target.value;
    var label = $('defectTypeLabel');
    if (!value) {
      if (label) label.style.visibility = 'hidden';
      e.target.style.color = '#929292';
      return;
    }
    if (label) label.style.visibility = 'visible';
    e.target.style.color = '#101010';
  }

  function onListingErrorChange(e) {
    var value = e.target.value;
    var listingErrorLabel = $('listingErrorLabel');
    if (!value) {
      if (listingErrorLabel) listingErrorLabel.style.visibility = 'hidden';
      e.target.style.color = '#929292';
      return;
    }
    if (listingErrorLabel) listingErrorLabel.style.visibility = 'visible';
    e.target.style.color = '#101010';
    // Always show description for listing errors (always mounted when this reason is selected).
  }

  // Selected images, kept left-packed: imageFiles[i] always renders in cell i.
  // The <input>s are just click targets — we read the pick, store it here and
  // reset the input, so removing a middle image just splices and re-renders.
  var IMAGE_IDS = ['reclaimImage1', 'reclaimImage2', 'reclaimImage3', 'reclaimImage4'];
  var imageFiles = [];
  var imageObjectUrls = [];

  function renderImageCells() {
    imageObjectUrls.forEach(function (url) { URL.revokeObjectURL(url); });
    imageObjectUrls = [];
    IMAGE_IDS.forEach(function (id, i) {
      var cell = $(id + 'PreviewUploading');
      var preview = $(id + 'Preview');
      var plus = $('loading' + id.charAt(0).toUpperCase() + id.slice(1) + 'Icon');
      var file = imageFiles[i];
      if (file) {
        var url = URL.createObjectURL(file);
        imageObjectUrls.push(url);
        if (cell) { cell.style.backgroundImage = "url('" + url + "')"; cell.classList.add('has-image'); }
        if (preview) preview.style.backgroundImage = "url('" + url + "')";
        if (plus) plus.style.display = 'none';
      } else {
        if (cell) { cell.style.backgroundImage = ''; cell.classList.remove('has-image'); }
        if (preview) preview.style.backgroundImage = '';
        if (plus) plus.style.display = '';
      }
    });
  }

  function wireImagePicker(id, index) {
    var input = $(id);
    if (!input) return;
    var removeBtn = $(id + 'Remove');

    input.addEventListener('change', function () {
      var file = input.files && input.files[0];
      if (!file) return;
      // Replace if this slot is already filled, otherwise append to the end.
      if (index < imageFiles.length) imageFiles[index] = file;
      else imageFiles.push(file);
      input.value = '';
      renderImageCells();
      refreshDoneButtonState();
    });

    if (removeBtn) {
      removeBtn.addEventListener('click', function (e) {
        // The cell is a <label>, so stop this click re-opening the file picker.
        e.preventDefault();
        e.stopPropagation();
        imageFiles.splice(index, 1);
        renderImageCells();
        refreshDoneButtonState();
      });
    }
  }

  function getFormImageFiles() {
    return imageFiles.slice();
  }

  // Returns the first validation failure as { el, message }, or null when the
  // form is valid. Pure: no setCustomValidity/reportValidity side effects, so it
  // can drive the "Klar" button's greyed-out state on every keystroke.
  function getValidationError() {
    var reasonEl = $('reclaimReason');
    if (!reasonEl.value) {
      return { el: reasonEl, message: locale.validation_select_reason };
    }
    var reason = reasonEl.value;

    // Reason-specific selects are marked `required` in onReasonChange.
    var defectTypeEl = $('reclaimDefectType');
    if (defectTypeEl && defectTypeEl.required && !defectTypeEl.value) {
      return { el: defectTypeEl, message: locale.validation_select_defect_type };
    }
    var listingErrorEl = $('reclaimListingError');
    if (listingErrorEl && listingErrorEl.required && !listingErrorEl.value) {
      return { el: listingErrorEl, message: locale.validation_select_listing_error };
    }

    // Defect type "Other" gives us no structured info — require a description.
    if (reason === 'UndisclosedDefects' && (($('reclaimDefectType') || {}).value === 'Other')) {
      var descEl = $('reclaimDescription');
      if (descEl && !descEl.value.trim()) {
        return { el: descEl, message: locale.validation_describe_defect };
      }
    }

    // Photos are mandatory for defects and wrong-item claims.
    if (reason === 'UndisclosedDefects' || reason === 'WrongItem') {
      if (getFormImageFiles().length === 0) {
        return { el: $('reclaimImage1'), message: locale.validation_upload_image };
      }
    }

    // A compensation choice is always required once a reason is selected.
    if (!getSelectedCompensation()) {
      var fullRefundRow = $('fullRefundRow');
      var compEl = fullRefundRow ? fullRefundRow.querySelector('input') : null;
      return { el: compEl, message: locale.validation_select_compensation };
    }

    // Custom discount needs a positive amount.
    if (getSelectedCompensation() === 'CustomDiscount') {
      var amtEl = $('customDiscountAmount');
      var amt = parseInt((amtEl || {}).value || '0', 10);
      if (!amt || amt < 1) {
        return { el: amtEl, message: locale.validation_custom_discount_amount };
      }
    }
    return null;
  }

  function validateInput() {
    var err = getValidationError();
    if (!err) return true;
    if (err.el) err.el.setCustomValidity(err.message);
    $('reclaimForm').reportValidity();
    return false;
  }

  // Grey out "Klar" while clicking it would only surface a validation error.
  // The button stays clickable so the error still shows, exactly as before.
  function refreshDoneButtonState() {
    var btn = $('doneButton');
    if (!btn) return;
    if (btn.classList.contains('loading')) return;
    var invalid = getValidationError() !== null;
    btn.classList.toggle('button--inactive', invalid);
    btn.setAttribute('aria-disabled', invalid ? 'true' : 'false');
  }

  function setDoneButtonLoading(loading) {
    var btn = $('doneButton');
    var spinner = $('doneButtonSpinner');
    if (!btn) return;
    if (loading) {
      btn.classList.remove('button--inactive');
      btn.removeAttribute('aria-disabled');
      btn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      btn.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
      refreshDoneButtonState();
    }
  }

  function getSelectedCompensation() {
    var radios = document.getElementsByName('compensationPreference');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].checked) return radios[i].value;
    }
    return '';
  }

  function saveReclaim(itemId) {
    if (!validateInput()) return Promise.resolve(false);

    // validateInput() (above) already guarantees the required selects, a
    // compensation choice and a valid custom amount, reporting any error.
    var reason = $('reclaimReason').value;
    var listingError = ($('reclaimListingError') || {}).value || '';
    var defectType = ($('reclaimDefectType') || {}).value || '';
    var description = ($('reclaimDescription') || {}).value || '';
    var compensationPreference = getSelectedCompensation();

    var refundAmount = null;
    if (compensationPreference === '10 percent discount') {
      var m = ($('discount10PercentText').innerText || '').match(/\d+/);
      if (m) refundAmount = parseInt(m[0], 10);
    } else if (compensationPreference === 'Full refund and return' || compensationPreference === 'Full refund') {
      refundAmount = soldPriceAmount;
    }

    var customDiscountAmount = null;
    if (compensationPreference === 'CustomDiscount') {
      customDiscountAmount = parseInt(($('customDiscountAmount') || {}).value || '0', 10);
    }

    var reclaim = {
      createdAt: new Date(),
      status: 'Pending',
      reason: reason,
      description: description,
      listingError: listingError,
      defectType: defectType,
      compensationPreference: compensationPreference,
      currency: itemCurrency
    };
    if (refundAmount !== null) reclaim.refundAmount = refundAmount;
    if (customDiscountAmount !== null) reclaim.customDiscountAmount = customDiscountAmount;

    // Record support-authorised late reclaims (?allowLate) so reviewers can see
    // the deadline was bypassed deliberately, and by whom if a tag was passed.
    var lateOverride = lateReclaimOverride();
    if (lateOverride) {
      reclaim.windowOverride = true;
      if (lateOverride !== '1' && lateOverride !== 'true') reclaim.windowOverrideTag = lateOverride;
    }

    setDoneButtonLoading(true);

    return Promise.all(
      getFormImageFiles().map(function (file, idx) {
        return uploadOneImage(itemId, file, idx).then(function (r) { return r.url; });
      })
    ).then(function (urls) {
      reclaim.images = urls;
      return postReclaim(itemId, reclaim);
    }).then(function () {
      return true;
    }).catch(function (err) {
      console.error('Failed to submit reclaim', err);
      setDoneButtonLoading(false);
      return false;
    });
  }

  function addEventListeners() {
    $('reclaimButton').addEventListener('click', function () {
      enableStickyBar();
      hide('introDiv');
      show('itemBanner', 'flex');
      show('howItWorksDiv', 'block');
      hideAllButtons();
      show('cancelButton', 'flex');
      show('nextButton', 'flex');
    });

    $('nextButton').addEventListener('click', function () {
      hide('howItWorksDiv');
      show('reclaimForm', 'block');
      hideAllButtons();
      show('cancelButton', 'flex');
      show('doneButton', 'flex');
      refreshDoneButtonState();
    });

    var reasonEl = $('reclaimReason');
    if (reasonEl) reasonEl.addEventListener('change', onReasonChange);
    var defectTypeEl = $('reclaimDefectType');
    if (defectTypeEl) defectTypeEl.addEventListener('change', onDefectTypeChange);
    var listingErrorEl = $('reclaimListingError');
    if (listingErrorEl) listingErrorEl.addEventListener('change', onListingErrorChange);
    var descriptionEl = $('reclaimDescription');
    if (descriptionEl) descriptionEl.addEventListener('input', refreshDescriptionLabel);

    var reclaimSubmitting = false;
    $('doneButton').addEventListener('click', function () {
      if (reclaimSubmitting) return;
      var itemId = getParam('id');
      reclaimSubmitting = true;
      saveReclaim(itemId).then(function (ok) {
        if (!ok) {
          reclaimSubmitting = false;
          return;
        }
        hide('reclaimForm');
        hideAllButtons();
        show('toMaiButton', 'flex');
        show('thankYouDiv', 'block');
        hide('itemBanner');
      }).catch(function (err) {
        reclaimSubmitting = false;
        setDoneButtonLoading(false);
        console.error('[reclaim] saveReclaim failed', err);
      });
    });

    IMAGE_IDS.forEach(function (id, i) { wireImagePicker(id, i); });

    // Show/hide custom discount amount input.
    document.querySelectorAll('[name="compensationPreference"]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        var container = $('customDiscountContainer');
        var input = $('customDiscountAmount');
        var isCustom = this.value === 'CustomDiscount';
        if (container) container.style.display = isCustom ? 'block' : 'none';
        if (input) input.required = isCustom;
      });
    });

    // Clear custom validity on any input.
    document.querySelectorAll('#reclaimFormInner input, #reclaimFormInner select, #reclaimFormInner textarea')
      .forEach(function (el) {
        var clear = function () { el.setCustomValidity(''); refreshDoneButtonState(); };
        el.addEventListener('input', clear);
        el.addEventListener('change', clear);
      });
  }

  // --------------------------------------------------------------------------
  // Boot
  // --------------------------------------------------------------------------

  function getSectionRoot() {
    return document.querySelector('[id^="reclaim-section-"]');
  }

  function hideLoading() {
    var section = getSectionRoot();
    if (!section) return;
    var loading = document.getElementById('reclaim-loading-' + section.dataset.sectionId);
    if (loading) loading.style.display = 'none';
  }

  function showContent() {
    hideLoading();
    var section = getSectionRoot();
    if (!section) return;
    var content = document.getElementById('reclaim-content-' + section.dataset.sectionId);
    if (content) content.hidden = false;
  }

  function showError() {
    hideLoading();
    var section = getSectionRoot();
    if (!section) return;
    var err = document.getElementById('reclaim-error-' + section.dataset.sectionId);
    if (err) err.hidden = false;
  }

  function init() {
    var itemId = getParam('id');
    if (!itemId) {
      console.error('[reclaim] Missing ?id param');
      showError();
      return;
    }
    getItem(itemId).then(function (item) {
      if (!item || !item.brand) {
        console.error('[reclaim] Item not found or missing brand field', item);
        showError();
        return;
      }
      initializePage(item);
      addEventListeners();
      if (getParam('preview') === 'thankyou') {
        enableStickyBar();
        hideAllButtons();
        show('toMaiButton', 'flex');
        hide('introDiv');
        show('thankYouDiv', 'block');
        show('itemBanner', 'flex');
      }
      showContent();
    }).catch(function (err) {
      console.error('[reclaim] Failed to load item', err);
      showError();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
